import React, { useState, useRef } from 'react';
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

interface ImageUploaderProps {
  onUpload: (file: File, onProgress?: (progress: number) => void) => Promise<string>; // Returns S3 key
  onRemove?: () => void;
  existingUrl?: string;
  label: string;
  accept?: string;
  maxSize?: number; // in MB
  required?: boolean;
  aspectRatio?: string;
  helperText?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUpload,
  onRemove,
  existingUrl,
  label,
  accept = 'image/*',
  maxSize = 5,
  required = false,
  aspectRatio = '16/9',
  helperText,
}) => {
  const [preview, setPreview] = useState<string | null>(existingUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload file with real progress tracking
      await onUpload(file, (progress) => {
        setUploadProgress(progress);
      });

      setUploadProgress(100);
      
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (err) {
      setError('Upload failed. Please try again.');
      setPreview(null);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onRemove?.();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-secondary-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative">
        {preview ? (
          <div className="relative group">
            <div
              className="w-full rounded-lg overflow-hidden border-2 border-secondary-200 bg-secondary-50"
              style={{ aspectRatio }}
            >
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              {isUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-white text-sm font-medium">{uploadProgress}%</p>
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleClick}
              className="absolute bottom-2 right-2 px-3 py-1.5 bg-white text-secondary-700 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-secondary-50 shadow-lg"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={handleClick}
              disabled={isUploading}
              className="w-full border-2 border-dashed border-secondary-300 rounded-lg hover:border-primary-500 transition-colors duration-200 bg-secondary-50 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ aspectRatio }}
            >
              {isUploading ? (
                <div className="flex flex-col items-center justify-center h-full p-6">
                  <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-sm font-medium text-secondary-700 mb-1">
                    Uploading... {uploadProgress}%
                  </p>
                  <div className="w-full max-w-xs bg-secondary-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-6">
                  <ArrowUpTrayIcon className="h-12 w-12 text-secondary-400 mb-3" />
                  <p className="text-sm font-medium text-secondary-700 mb-1">
                    Click to upload {label.toLowerCase()}
                  </p>
                  <p className="text-xs text-secondary-500">
                    PNG, JPG up to {maxSize}MB
                  </p>
                </div>
              )}
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {helperText && !error && (
        <p className="text-xs text-secondary-500">{helperText}</p>
      )}

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

